import { INQUIRED_STATUS_REASON_OPTIONS } from '../../utilityFunctions';

export const LEAD_QUALITY_UNCATEGORIZED_REASON = 'No reason recorded';
export const QUALIFIED_LEAD_CATEGORY = 'Qualified';
export const UNQUALIFIED_LEAD_REASONS = [
  'Too young',
  'Pricing',
  'Instrument not available',
  'Wants in-studio',
  'Only wants intro',
  'Out-of-range',
];

const DAY_MS = 24 * 60 * 60 * 1000;

const normalizeText = (value) => String(value == null ? '' : value).trim();
const normalizeKey = (value) => normalizeText(value).toLowerCase().replace(/\s+/g, ' ');
const normalizeDispositionKey = (value) =>
  normalizeKey(value).replace(/[-–—]+/g, ' ').replace(/\s+/g, ' ');

const UNQUALIFIED_REASON_BY_KEY = new Map(
  UNQUALIFIED_LEAD_REASONS.map((reason) => [normalizeDispositionKey(reason), reason])
);

const toValidDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getFamilyId = (family) =>
  normalizeText(family?.familyId || family?.FamilyID || family?._id || family?.id);

const getLogFamilyId = (log) => normalizeText(log?.familyId || log?.FamilyID);

const getInquiryDate = (family, earliestFamilyLog) =>
  toValidDate(
    family?.onboardDate ||
      family?.inquiryDate ||
      family?.createdAt ||
      earliestFamilyLog?.effectiveDate ||
      earliestFamilyLog?.date
  );

const getRangeStart = (range, now) => {
  if (range === 'all') return null;
  if (range === 'ytd') return new Date(now.getFullYear(), 0, 1);

  const days = Number(range);
  if (!Number.isFinite(days) || days <= 0) return null;

  const start = new Date(now.getTime() - (days - 1) * DAY_MS);
  start.setHours(0, 0, 0, 0);
  return start;
};

export const normalizeLeadIssueReason = (reason) => {
  const trimmedReason = normalizeText(reason);
  if (!trimmedReason) return LEAD_QUALITY_UNCATEGORIZED_REASON;

  const canonicalReason = INQUIRED_STATUS_REASON_OPTIONS.find(
    (option) => normalizeKey(option) === normalizeKey(trimmedReason)
  );

  return canonicalReason || trimmedReason;
};

export const getUnqualifiedLeadReason = ({ reason, familyStatus } = {}) => {
  const statusKey = normalizeDispositionKey(familyStatus);
  if (statusKey === 'out of range') return 'Out-of-range';
  return UNQUALIFIED_REASON_BY_KEY.get(normalizeDispositionKey(reason)) || null;
};

const isFamilyStatusLog = (log) => {
  const entityType = normalizeKey(log?.entityType);
  return !entityType || entityType === 'family';
};

const isInquiredLog = (log) => normalizeKey(log?.status) === 'inquired';

const compareIssueRows = (a, b) => {
  const aTime = a.issueDate?.getTime?.() || 0;
  const bTime = b.issueDate?.getTime?.() || 0;
  if (aTime !== bTime) return bTime - aTime;
  return a.familyName.localeCompare(b.familyName);
};

export const buildInquiryLeadQuality = ({
  families = [],
  logs = [],
  range = '90',
  school = 'ALL',
  now = new Date(),
} = {}) => {
  const safeFamilies = Array.isArray(families) ? families : [];
  const safeLogs = Array.isArray(logs) ? logs : [];
  const normalizedSchool = normalizeKey(school);
  const requestedRangeStart = getRangeStart(range, now);
  const rangeEnd = new Date(now);
  rangeEnd.setHours(23, 59, 59, 999);

  const familyLogsById = new Map();
  safeLogs.forEach((log) => {
    if (!isFamilyStatusLog(log)) return;
    const familyId = getLogFamilyId(log);
    if (!familyId) return;
    if (!familyLogsById.has(familyId)) familyLogsById.set(familyId, []);
    familyLogsById.get(familyId).push(log);
  });

  familyLogsById.forEach((familyLogs) => {
    familyLogs.sort((a, b) => {
      const aDate = toValidDate(a?.effectiveDate || a?.date)?.getTime() || 0;
      const bDate = toValidDate(b?.effectiveDate || b?.date)?.getTime() || 0;
      return aDate - bDate;
    });
  });

  const trackingStartDate = safeLogs.reduce((earliestDate, log) => {
    if (!isFamilyStatusLog(log)) return earliestDate;
    const logDate = toValidDate(log?.effectiveDate || log?.date);
    if (!logDate) return earliestDate;
    if (!earliestDate || logDate < earliestDate) return logDate;
    return earliestDate;
  }, null);
  const rangeStart =
    trackingStartDate && (!requestedRangeStart || trackingStartDate > requestedRangeStart)
      ? trackingStartDate
      : requestedRangeStart;

  const rows = [];
  let excludedMissingDate = 0;

  safeFamilies.forEach((family) => {
    const familyId = getFamilyId(family);
    if (!familyId) return;

    const familySchool = normalizeText(family?.school || family?.School);
    if (
      normalizedSchool &&
      normalizedSchool !== 'all' &&
      normalizeKey(familySchool) !== normalizedSchool
    ) {
      return;
    }

    const familyLogs = familyLogsById.get(familyId) || [];
    const inquiryDate = getInquiryDate(family, familyLogs[0]);
    if (!inquiryDate) {
      excludedMissingDate += 1;
      return;
    }
    if (rangeStart && inquiryDate < rangeStart) return;
    if (inquiryDate > rangeEnd) return;

    const inquiredLogs = familyLogs.filter(isInquiredLog);
    const latestIssueLog = inquiredLogs.length ? inquiredLogs[inquiredLogs.length - 1] : null;
    const latestReasonLog = [...inquiredLogs]
      .reverse()
      .find((log) => normalizeText(log?.reason));
    const fallbackReason =
      normalizeKey(family?.familyStatus) === 'inquired' ? family?.inquiredReason : '';
    const hasRecordedIssue = Boolean(latestIssueLog || normalizeText(fallbackReason));
    const issueDate = latestIssueLog
      ? toValidDate(latestIssueLog.effectiveDate || latestIssueLog.date)
      : null;
    const reason = hasRecordedIssue
      ? normalizeLeadIssueReason(latestReasonLog?.reason || fallbackReason)
      : null;
    const familyStatus = normalizeText(family?.familyStatus);
    const unqualifiedReason = getUnqualifiedLeadReason({ reason, familyStatus });

    rows.push({
      familyId,
      familyName: normalizeText(family?.familyName) || 'Unnamed family',
      school: familySchool || normalizeText(latestIssueLog?.school) || '—',
      inquiryDate,
      issueDate,
      reason,
      hasRecordedIssue,
      familyStatus,
      unqualifiedReason,
      isQualified: !unqualifiedReason,
    });
  });

  const issueRows = rows.filter((row) => row.hasRecordedIssue).sort(compareIssueRows);
  const goodLeadRows = rows.filter((row) => !row.hasRecordedIssue);
  const qualifiedRows = rows.filter((row) => row.isQualified);
  const unqualifiedRows = rows.filter((row) => !row.isQualified);
  const reasonCounts = new Map();

  issueRows.forEach((row) => {
    reasonCounts.set(row.reason, (reasonCounts.get(row.reason) || 0) + 1);
  });

  const canonicalOrder = new Map(
    [...INQUIRED_STATUS_REASON_OPTIONS, LEAD_QUALITY_UNCATEGORIZED_REASON].map(
      (reason, index) => [reason, index]
    )
  );
  const reasonBreakdown = Array.from(reasonCounts.entries())
    .map(([reason, count]) => ({
      reason,
      count,
      percentOfIssues: issueRows.length ? (count / issueRows.length) * 100 : 0,
      percentOfTotal: rows.length ? (count / rows.length) * 100 : 0,
    }))
    .sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count;
      const aOrder = canonicalOrder.has(a.reason) ? canonicalOrder.get(a.reason) : Infinity;
      const bOrder = canonicalOrder.has(b.reason) ? canonicalOrder.get(b.reason) : Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.reason.localeCompare(b.reason);
    });

  const total = rows.length;
  const issueCount = issueRows.length;
  const goodLeadCount = goodLeadRows.length;
  const qualifiedCount = qualifiedRows.length;
  const unqualifiedCount = unqualifiedRows.length;
  const leadDistribution = [
    {
      category: QUALIFIED_LEAD_CATEGORY,
      count: qualifiedCount,
      percent: total ? (qualifiedCount / total) * 100 : 0,
    },
    ...UNQUALIFIED_LEAD_REASONS.map((category) => {
      const count = unqualifiedRows.filter((row) => row.unqualifiedReason === category).length;
      return {
        category,
        count,
        percent: total ? (count / total) * 100 : 0,
      };
    }),
  ];

  return {
    total,
    issueCount,
    goodLeadCount,
    issueRate: total ? (issueCount / total) * 100 : 0,
    goodLeadRate: total ? (goodLeadCount / total) * 100 : 0,
    qualifiedCount,
    qualifiedRate: total ? (qualifiedCount / total) * 100 : 0,
    unqualifiedCount,
    unqualifiedRate: total ? (unqualifiedCount / total) * 100 : 0,
    rows,
    issueRows,
    goodLeadRows,
    qualifiedRows,
    unqualifiedRows,
    leadDistribution,
    reasonBreakdown,
    excludedMissingDate,
    trackingStartDate,
  };
};
